import {
  CircleDollarSign,
  CodeXml,
  LayoutPanelTop,
  Newspaper,
  Package,
  Palette,
  PencilRuler,
  Shapes,
  ShoppingCart,
  Split,
  Tag,
  Truck,
} from "lucide-react";
import React, { type JSX } from "react";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { Box } from "@mui/material";

interface TestTypeIconProps {
  testFlowType: string;
  fontSize?: string;
  isPersonalization?: boolean;
  showTitle?: boolean;
  color?: string;
  hideTooltip?: boolean;
}

export const TestTypeIcon = ({
  testFlowType,
  fontSize,
  isPersonalization,
  showTitle,
  color,
  hideTooltip = false,
}: TestTypeIconProps) => {
  let testTypeObject: { title: string; icon: JSX.Element };
  switch (testFlowType) {
    case "content/advanced":
      testTypeObject = {
        title: "Advanced Content Test",
        icon: <Newspaper size={fontSize ?? 20} color={color} />,
      };
      break;
    case "content/onsiteEdits":
      // @ts-ignore
      testTypeObject = {
        title: isPersonalization
          ? "Content Edit Modification"
          : "Onsite Edit Test",
        icon: <PencilRuler size={fontSize ?? 20} color={color} />,
      };
      break;
    case "content/url":
      testTypeObject = {
        title: `URL Redirect ${isPersonalization ? "Modification" : "Test"}`,
        icon: <Split size={fontSize ?? 20} color={color} />,
      };
      break;
    case "content/theme":
      testTypeObject = {
        title: `Theme ${isPersonalization ? "Modification" : "Test"}`,
        icon: <Palette size={fontSize ?? 20} color={color} />,
      };
      break;
    case "content/template":
      testTypeObject = {
        title: `Template ${isPersonalization ? "Modification" : "Test"}`,
        icon: <LayoutPanelTop size={fontSize ?? 20} color={color} />,
      };
      break;
    case "pricing":
      testTypeObject = {
        title: `Price ${isPersonalization ? "Modification" : "Test"}`,
        icon: <CircleDollarSign size={fontSize ?? 20} color={color} />,
      };
      break;
    case "personalization/offer":
      testTypeObject = {
        title: `Offer ${isPersonalization ? "Modification" : "Test"}`,
        icon: <Tag size={fontSize ?? 20} color={color} />,
      };
      break;
    case "shipping":
      testTypeObject = {
        title: "Shipping Test",
        icon: <Truck size={fontSize ?? 20} color={color} />,
      };
      break;
    case "content/onsiteInjections":
      testTypeObject = {
        title: "Styles & Javascript",
        icon: <CodeXml size={fontSize ?? 20} color={color} />,
      };
      break;
    case "checkoutBlock":
      testTypeObject = {
        title: "Checkout Test",
        icon: <Shapes size={fontSize ?? 20} color={color} />,
      };
      break;
    case "postPurchase":
      testTypeObject = {
        title: "Post-Purchase",
        icon: <ShoppingCart size={fontSize ?? 20} color={color} />,
      };
      break;
    case "smartMerchandising":
      testTypeObject = {
        title: "Smart Merchandising",
        icon: <Package size={fontSize ?? 20} color={color} />,
      };
      break;
    case "onsiteUpsell":
      testTypeObject = {
        title: `Onsite Upsell ${isPersonalization ? "Personalization" : "Test"}`,
        icon: <Newspaper size={fontSize ?? 20} color={color} />,
      };
      break;
    default:
      testTypeObject = {
        title: "Advanced Test",
        icon: <Newspaper size={fontSize ?? 20} color={color} />,
      };
      break;
  }
  if (!testTypeObject.icon) {
    return null;
  }

  const content = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {testTypeObject.icon}
      {showTitle ? (
        <IgTypography variant={"h2"}>{testTypeObject.title}</IgTypography>
      ) : null}
    </Box>
  );

  if (hideTooltip) {
    return content;
  }

  return <IgTooltip title={testTypeObject.title}>{content}</IgTooltip>;
};
